import { useState, useEffect, useRef, useCallback } from 'react';
import { NativeCarousel, NativeCarouselItem } from '@worldcoo/ui/components/native-carousel';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePortalMetadataQuery } from '@/lib/queries';
import { isNotFound } from '@tanstack/react-router';
import { generateImageSrc } from '@/lib/shared';

const sliderImageSize = { width: 1280, height: 840 };

export function TestimonialSlider() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	const { sliderItems } = metadata;
	if (!sliderItems || sliderItems.length === 0) return null;

	return <SliderCarousel items={sliderItems} />;
}

function SliderCarousel({
	items,
}: {
	items: {
		image?: { location: string; width: number; height: number };
		quote: string;
		authorName: string;
		imageAlt?: string;
	}[];
}) {
	const [current, setCurrent] = useState(0);
	const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

	useEffect(() => {
		const observers: IntersectionObserver[] = [];

		itemRefs.current.forEach((el, index) => {
			if (!el) return;
			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0]?.isIntersecting) setCurrent(index);
				},
				{ threshold: 0.5 },
			);
			observer.observe(el);
			observers.push(observer);
		});

		return () => observers.forEach((o) => o.disconnect());
	}, [items.length]);

	const scrollTo = useCallback((index: number) => {
		itemRefs.current[index]?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'center',
		});
	}, []);

	return (
		<section className="mx-auto w-full max-w-[1366px] px-6 py-16 lg:px-20 lg:py-24">
			<div className="relative overflow-hidden rounded-2xl">
				<NativeCarousel className="gap-0 rounded-2xl px-0 [&::scroll-button(*)]:hidden">
					{items.map((item, index) => (
						<NativeCarouselItem
							key={item.authorName}
							ref={(el) => {
								itemRefs.current[index] = el;
							}}
							className="w-full"
						>
							<div className="relative h-[480px] w-full lg:h-[640px]">
								{item.image?.location && (
									<img
										src={generateImageSrc(item.image.location, sliderImageSize)}
										alt={item.imageAlt ?? item.authorName}
										className="absolute inset-0 size-full object-cover"
										loading="lazy"
									/>
								)}
								<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
								<div className="absolute right-8 bottom-8 left-8 max-w-3xl lg:right-16 lg:bottom-12 lg:left-16">
									<p className="text-2xl leading-tight font-semibold tracking-tight text-white lg:text-[40px] lg:leading-[1.15]">
										{item.quote}
									</p>
									<p className="mt-2 text-base font-medium text-white lg:text-xl">{item.authorName}</p>
								</div>
							</div>
						</NativeCarouselItem>
					))}
				</NativeCarousel>

				{items.length > 1 && (
					<div className="absolute right-8 bottom-8 flex gap-2 lg:right-16 lg:bottom-12">
						{items.map((item, dotIndex) => (
							<button
								key={item.authorName}
								type="button"
								onClick={() => scrollTo(dotIndex)}
								className={cn(
									'size-2.5 rounded-full transition-colors',
									dotIndex === current ? 'bg-white' : 'bg-white/40',
								)}
								aria-label={`Go to slide ${dotIndex + 1}`}
							/>
						))}
					</div>
				)}
			</div>
		</section>
	);
}
